import { useId } from "react";

import { Carousel } from "../../../../core/Carousel.js";
import { CAROUSEL_SLIDES } from "../../../../core/Carousel.styles.js";
import { Text } from "../../../../core/Text.js";
import { ItemCard } from "../../../../domain/ItemCard.js";
import { RecentHits } from "../../../../domain/RecentHits.js";
import { RARITY_LABELS } from "../../../../domain/rarity.js";
import { Section } from "../../../../patterns/Section.js";
import type { LiveHitsContent } from "../../homeContent.js";

/**
 * Other shoppers' latest big pulls, live. The `strip` variant: a glass strip pulled up into the hero's edge.
 * `cards`: each pull an ItemCard (pull) in its tier, with who pulled it, on a carousel under the section's
 * title and its live marker.
 */
export function HomeLiveHits({
  variant = "strip",
  label,
  liveLabel,
  hits,
}: LiveHitsContent & { variant?: "strip" | "cards" }) {
  const id = useId();
  if (hits.length === 0) return null;
  if (variant === "cards") {
    const heading = (
      <Text
        as="h2"
        id={id}
        variant="display-xl3"
        tone="primary"
        className="flex items-center gap-3 max-md:text-display-xl4"
      >
        {label}
        {liveLabel && (
          <span className="flex items-center gap-1.5 font-mono text-mono-meta text-status-danger uppercase">
            <span
              aria-hidden
              className="size-1.5 rounded-pill bg-status-danger motion-safe:animate-pulse"
            />
            {liveLabel}
          </span>
        )}
      </Text>
    );
    return (
      // The Carousel is the region, named by the label, so the section names nothing.
      <Section>
        <Carousel label={label} heading={heading} slideClassName={CAROUSEL_SLIDES.cards}>
          {hits.map((hit, i) => {
            const tier = hit.tier ?? "common";
            const tierLabel = hit.tierLabel ?? RARITY_LABELS[tier];
            return (
              <ItemCard
                variant="pull"
                key={hit.id}
                name={hit.itemName}
                image={hit.image}
                cents={hit.value}
                tier={tier}
                tierLabel={tierLabel}
                player={{ name: hit.username }}
                href={hit.href}
                seed={i}
                priority={i < 6}
              />
            );
          })}
        </Carousel>
      </Section>
    );
  }
  // Pulled up into the hero's bottom edge, as the strip sits in the Figma.
  return (
    <Section className="-mt-6">
      <RecentHits label={label} liveLabel={liveLabel} hits={hits} />
    </Section>
  );
}
